import { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';

import { selectGame, selectLoaderProgress } from 'src/slices/game.slice';
import { secondsToHHMMSS } from 'src/utils/timeHelpers';
import { selectGameEvents } from 'src/slices/gameEvents.slice';
import { isLoaderExperimentEnabled } from 'src/utils/url';
import { formatBytes } from 'src/utils/loader';

import Stat from 'src/components/ui/Stat';
import LanguageSelector from 'src/components/ui/LanguageSelector';

import { SDK_EVENTS, SIZE_FORMAT_PLACEHOLDER, TIME_FORMAT_PLACEHOLDER } from 'src/vars';

const GameDetailsWrapper = styled.div`
    display: flex;
    justify-content: space-between;
    flex-direction: column;
    gap: 24px;
    border-radius: 0 0 8px 8px;
    padding: 16px;
    background: var(--surface);
`;

const GameDetailsRow = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
`;

const GameDetailsStats = styled.div`
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    width: 100%;
    gap: 8px;
`;

const GameDetailsTitle = styled.h2`
    font-size: 21px;
    color: var(--text-primary);
    margin: 0;
`;

const getLoadingFinishedProperties = (gameEvents) => {
    const loadingFinishedEvent = gameEvents.find((e) => (
        e.event === SDK_EVENTS.GAME_LOADING_FINISHED
    ));

    return {
        timestamp: loadingFinishedEvent?.timestamp || null,
        transferSize: loadingFinishedEvent?.data?.transferSize || 0,
    };
};

const getLoaderTransferSize = (loaderProgress) => {
    const loadedBytes = Number.isFinite(loaderProgress.loadedBytes) ? loaderProgress.loadedBytes : 0;
    const totalBytes = Number.isFinite(loaderProgress.totalBytes) ? loaderProgress.totalBytes : 0;

    return Math.max(loadedBytes, totalBytes);
};

const getFormattedFileSize = (size) => {
    if (!size) {
        return SIZE_FORMAT_PLACEHOLDER;
    }

    return formatBytes(size);
};

export default function GameDetails() {
    const game = useSelector(selectGame);
    const gameEvents = useSelector(selectGameEvents);
    const loaderProgress = useSelector(selectLoaderProgress);

    const [initialTimestamp] = useState(() => Date.now());

    const { timestamp: loadingFinishedTimestamp, transferSize: loadingFinishedTransferSize } = getLoadingFinishedProperties(gameEvents);
    const loaderTransferSize = isLoaderExperimentEnabled() ? getLoaderTransferSize(loaderProgress) : 0;

    // When the loader experiment is enabled, we want to show the loader transfer size (much more accurate).
    // Otherwise, we fall back to the transfer size from the loading finished event, if available.
    const transferSize = loaderTransferSize || loadingFinishedTransferSize || null;

    const loadingTime = useMemo(() => {
        if (!loadingFinishedTimestamp) return null;

        const initialUnixTimestamp = initialTimestamp / 1000;
        const finalUnixTimestamp = loadingFinishedTimestamp / 1000;
        const totalLoadingTime = finalUnixTimestamp - initialUnixTimestamp;

        if (totalLoadingTime < 1) {
            return '00:00:01';
        }

        return secondsToHHMMSS(totalLoadingTime);
    }, [initialTimestamp, loadingFinishedTimestamp]);

    const roundedFileSize = getFormattedFileSize(game.archiveSize);
    const roundedTransferSize = getFormattedFileSize(transferSize);

    return (
        <GameDetailsWrapper>
            <GameDetailsRow>
                <GameDetailsTitle>
                    {game?.gameTitle || game?.originalFilename}
                </GameDetailsTitle>
                <LanguageSelector />
            </GameDetailsRow>
            <GameDetailsStats>
                <Stat label="Total loading time" value={loadingTime || TIME_FORMAT_PLACEHOLDER} />
                <Stat label="File size" value={roundedFileSize || SIZE_FORMAT_PLACEHOLDER} />
                <Stat label="Initial download size" value={roundedTransferSize} />
                <Stat />
            </GameDetailsStats>
        </GameDetailsWrapper>
    );
}
